const { Core, Events, Utils } = Global;
const { $toggleDisplay } = Utils;
const CN_ACTIVE = '!active';
const CN_LOADING = '!loading';
const MOBILE_BREAKPOINT = 991;
const GORGIAS_LAUNCHER_TOP = 96;
const GORGIAS_LAUNCHER_LEFT = 68;
const GORGIAS_LAUNCHER_SIZE = 44;
const GORGIAS_CAMPAIGN_GAP = 16;
const GORGIAS_CAMPAIGN_HEIGHT = 44;
const GORGIAS_CAMPAIGN_RIGHT_RESERVE = 66;

customElements.define('sticky-add-to-cart', class extends Core {
    elements = { $: ['observer-trigger', 'wrapper'] }
    propTypes = { 'sticky-bottom': Boolean }

    render() {
        this.sub(Events.VARIANT_CHANGE, this._toggleLoading(true));
        this.sub(Events.VARIANT_UPDATE, this._toggleLoading(false));
        this.sub(Events.VARIANT_UNAVAILABLE, this._handleVariantUnavailability);

        this._boundResize = this._handleResize.bind(this);
        this._lastMobileState = this._isMobile();

        this._setupFloatingTools();
        this._setupCustomGorgiasLauncher();
        this._handleTrigger();

        window.addEventListener('resize', this._boundResize, { passive: true });
    }

    disconnectedCallback() {
        this.observer?.disconnect();
        window.removeEventListener('resize', this._boundResize);

        this._floatingToolsObserver?.disconnect();
        this._gorgiasDomObserver?.disconnect();

        window.removeEventListener('gorgias-widget-loaded', this._boundGorgiasLoaded);

        this._removeCustomWhatsAppLauncher();
        this._restoreNativeWhatsAppLauncher();

        this._removeCustomGorgiasLauncher();
        this._restoreNativeGorgiasLauncher();
        this._restoreGorgiasCampaigns();

        document.body.classList.remove('doto-sticky-buy-visible');
        document.body.style.removeProperty('--bottom-offset');

        if (super.disconnectedCallback) super.disconnectedCallback();
    }

    _isMobile() {
        return window.innerWidth <= MOBILE_BREAKPOINT;
    }

    _getProductSection() {
        const sectionId = this.getAttribute('section-id');
        if (!sectionId) return null;

        return document.querySelector(`#shopify-section-${sectionId}`);
    }

    _handleResize() {
        window.requestAnimationFrame(() => {
            const mobileState = this._isMobile();

            if (mobileState !== this._lastMobileState) {
                this._lastMobileState = mobileState;
                this.active = false;
                this._handleTrigger();
            }

            if (this.classList.contains(CN_ACTIVE)) {
                this._syncBodyOffset(true);
            }

            this._syncCustomWhatsAppLauncher();
            this._syncCustomGorgiasLauncher();
            this._syncGorgiasCampaigns();
        });
    }

    _handleTrigger() {
        this.observer?.disconnect();
        this.active = false;

        const section = this._getProductSection();
        if (!section) return;

        if (this._isMobile()) {
            this._handleMobileTrigger(section);
        } else {
            this._handleDesktopTrigger(section);
        }
    }

    _handleMobileTrigger(section) {
        const buyNowButton = section.querySelector('.btnComprarAhora');
        const purchaseTrigger =
            buyNowButton?.parentElement ||
            section.querySelector('#b-buttons') ||
            section.querySelector('.\\#product-form-add-to-cart') ||
            section.querySelector('[data-element="to-cart-button"]')?.closest('form') ||
            section.querySelector('form.shopify-product-form');

        if (!purchaseTrigger) return;

        this.observer = new IntersectionObserver(([entry]) => {
            if (!this._isMobile()) return;

            const hasPassedPurchaseArea =
                !entry.isIntersecting &&
                entry.boundingClientRect.bottom <= 0;

            this.active = hasPassedPurchaseArea;
        }, {
            root: null,
            rootMargin: '0px',
            threshold: [0, 1]
        });

        this.observer.observe(purchaseTrigger);
    }

    _handleDesktopTrigger(section) {
        const addToCartForm = section.querySelector('.\\#product-form-add-to-cart');

        if (addToCartForm) {
            this._observeDesktopElement(addToCartForm);
            return;
        }

        const alternativeForm =
            section.querySelector('[data-element="to-cart-button"]')?.closest('form') ||
            section.querySelector('product-to-cart')?.closest('.form') ||
            section.querySelector('form.shopify-product-form');

        if (alternativeForm) {
            this._observeDesktopElement(alternativeForm);
        }
    }

    _observeDesktopElement(element) {
        this.observer = new IntersectionObserver(([entry]) => {
            if (this._isMobile()) return;

            const hasPassedForm =
                !entry.isIntersecting &&
                entry.boundingClientRect.top <= 0;

            this.active = hasPassedForm;
        }, {
            root: null,
            rootMargin: '0px',
            threshold: 0.1
        });

        this.observer.observe(element);
    }

    _syncBodyOffset(state) {
        if (!this.prop('sticky-bottom')) return;

        if (!state) {
            document.body.style.removeProperty('--bottom-offset');
            return;
        }

        const wrapper = this.$('wrapper');
        if (!wrapper) return;

        document.body.style.setProperty('--bottom-offset', `${wrapper.offsetHeight}px`);
    }

    /* ======================================================
       WHATSAPP — THEME CONTROLLED PDP MOBILE LAUNCHER
       ====================================================== */

    _setupFloatingTools() {
        this._syncCustomWhatsAppLauncher();

        this._floatingToolsObserver = new MutationObserver(() => {
            if (this._floatingToolsScanQueued) return;

            this._floatingToolsScanQueued = true;

            window.requestAnimationFrame(() => {
                this._floatingToolsScanQueued = false;
                this._syncCustomWhatsAppLauncher();
            });
        });

        this._floatingToolsObserver.observe(document.body, {
            childList: true,
            subtree: true
        });
    }

    _getWhatsAppCandidates() {
        return Array.from(document.querySelectorAll([
            'a[href*="wa.me"]',
            'a[href*="whatsapp.com"]',
            'a[href*="api.whatsapp.com"]',
            '[aria-label*="whatsapp" i]',
            '[title*="whatsapp" i]',
            '[class*="whatsapp" i]',
            '[id*="whatsapp" i]'
        ].join(',')))
        .filter((node) => !node.closest('.doto-whatsapp-launcher'));
    }

    _findWhatsAppUrl() {
        const candidates = this._getWhatsAppCandidates();

        for (const candidate of candidates) {
            const link = candidate.matches('a[href]')
                ? candidate
                : candidate.closest('a[href]') || candidate.querySelector?.('a[href]');

            const href = link?.getAttribute('href');

            if (
                href &&
                (
                    href.includes('wa.me') ||
                    href.includes('whatsapp.com') ||
                    href.includes('api.whatsapp.com')
                )
            ) {
                return href;
            }
        }

        return 'https://api.whatsapp.com/send?phone=5215588973686&text=Hola%2C%20Necesito%20ayuda';
    }

    _hideNativeWhatsAppLauncher() {
        this._getWhatsAppCandidates().forEach((candidate) => {
            const root = this._findFixedAncestor(candidate) || candidate;

            if (root.classList?.contains('doto-whatsapp-launcher')) return;

            root.setAttribute('data-doto-whatsapp-native-launcher', '');
            root.style.setProperty('display', 'none', 'important');
            root.style.setProperty('visibility', 'hidden', 'important');
            root.style.setProperty('pointer-events', 'none', 'important');
        });
    }

    _restoreNativeWhatsAppLauncher() {
        document.querySelectorAll('[data-doto-whatsapp-native-launcher]').forEach((launcher) => {
            launcher.style.removeProperty('display');
            launcher.style.removeProperty('visibility');
            launcher.style.removeProperty('pointer-events');
            launcher.removeAttribute('data-doto-whatsapp-native-launcher');
        });
    }

    _syncCustomWhatsAppLauncher() {
        if (!this._isMobile()) {
            this._removeCustomWhatsAppLauncher();
            this._restoreNativeWhatsAppLauncher();
            return;
        }

        const whatsappUrl = this._findWhatsAppUrl();

        this._hideNativeWhatsAppLauncher();

        if (this._customWhatsAppLauncher?.isConnected) {
            this._customWhatsAppLauncher.href = whatsappUrl;
            return;
        }

        const link = document.createElement('a');
        link.className = 'doto-whatsapp-launcher';
        link.href = whatsappUrl;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.setAttribute('aria-label', 'Abrir WhatsApp');
        link.innerHTML = `
            <svg viewBox="0 0 32 32" aria-hidden="true" focusable="false">
                <path
                    fill="currentColor"
                    d="M16.02 3.2A12.61 12.61 0 0 0 5.2 22.3L3.5 28.5l6.34-1.66A12.6 12.6 0 1 0 16.02 3.2Zm0 22.94a10.3 10.3 0 0 1-5.25-1.44l-.38-.23-3.76.98 1-3.65-.25-.38a10.34 10.34 0 1 1 8.64 4.72Zm5.67-7.74c-.31-.16-1.84-.91-2.13-1.01-.28-.1-.49-.16-.7.16-.2.31-.8 1.01-.98 1.22-.18.21-.36.23-.67.08-.31-.15-1.31-.48-2.5-1.55a9.34 9.34 0 0 1-1.73-2.15c-.18-.31-.02-.48.14-.64.14-.14.31-.36.47-.54.15-.18.2-.31.31-.52.1-.2.05-.39-.03-.54-.08-.16-.7-1.69-.96-2.31-.25-.61-.51-.53-.7-.54h-.6c-.21 0-.54.08-.83.39-.28.31-1.09 1.07-1.09 2.6 0 1.53 1.12 3.01 1.27 3.22.16.21 2.2 3.36 5.33 4.71.74.32 1.33.51 1.78.65.75.24 1.43.2 1.97.12.6-.09 1.84-.75 2.1-1.48.26-.73.26-1.35.18-1.48-.08-.13-.28-.21-.6-.36Z"
                />
            </svg>
        `;

        document.body.appendChild(link);
        this._customWhatsAppLauncher = link;
    }

    _removeCustomWhatsAppLauncher() {
        this._customWhatsAppLauncher?.remove();
        this._customWhatsAppLauncher = null;
    }

    /* ======================================================
       GORGIAS — THEME CONTROLLED PDP MOBILE LAUNCHER
       ====================================================== */

    _setupCustomGorgiasLauncher() {
        this._boundGorgiasLoaded = () => {
            this._syncCustomGorgiasLauncher();
            this._syncGorgiasCampaigns();
        };

        window.addEventListener(
            'gorgias-widget-loaded',
            this._boundGorgiasLoaded
        );

        this._gorgiasDomObserver = new MutationObserver(() => {
            this._syncCustomGorgiasLauncher();
            this._syncGorgiasCampaigns();
        });

        this._gorgiasDomObserver.observe(document.documentElement, {
            childList: true,
            subtree: true
        });

        this._syncCustomGorgiasLauncher();
        this._syncGorgiasCampaigns();
    }

    _getGorgiasInitPromise() {
        if (window.GorgiasChat) {
            try {
                return window.GorgiasChat.init();
            } catch (error) {
                return Promise.resolve();
            }
        }

        return new Promise((resolve) => {
            window.addEventListener(
                'gorgias-widget-loaded',
                resolve,
                { once: true }
            );
        });
    }

    _syncCustomGorgiasLauncher() {
        if (!this._isMobile()) {
            this._removeCustomGorgiasLauncher();
            this._restoreNativeGorgiasLauncher();
            return;
        }

        this._hideNativeGorgiasLauncher();
        this._syncGorgiasCampaigns();

        if (this._customGorgiasLauncher?.isConnected) return;

        this._getGorgiasInitPromise().then(() => {
            if (!this._isMobile()) return;

            this._hideNativeGorgiasLauncher();
            this._createCustomGorgiasLauncher();
            this._syncGorgiasCampaigns();
        });
    }

    _getGorgiasCampaignFrames() {
        return Array.from(document.querySelectorAll(
            '#gorgias-chat-container iframe#chat-campaigns, iframe#chat-campaigns'
        ));
    }

    _syncGorgiasCampaigns() {
        const frames = this._getGorgiasCampaignFrames();

        if (!this._isMobile()) {
            this._restoreGorgiasCampaigns();
            return;
        }

        const compact = window.innerWidth <= 380;
        const top = compact ? 90 : GORGIAS_LAUNCHER_TOP;
        const launcherLeft = compact ? 62 : GORGIAS_LAUNCHER_LEFT;
        const launcherSize = compact ? 42 : GORGIAS_LAUNCHER_SIZE;
        const left = launcherLeft + launcherSize + GORGIAS_CAMPAIGN_GAP;
        const rightReserve = compact ? 58 : GORGIAS_CAMPAIGN_RIGHT_RESERVE;
        const availableWidth = Math.max(150, window.innerWidth - left - rightReserve);
        const width = Math.min(220, availableWidth);
        const height = compact ? 42 : GORGIAS_CAMPAIGN_HEIGHT;

        frames.forEach((frame) => {
            frame.setAttribute('data-doto-gorgias-campaigns', '');
            frame.style.setProperty('position', 'fixed', 'important');
            frame.style.setProperty('top', `${top}px`, 'important');
            frame.style.setProperty('left', `${left}px`, 'important');
            frame.style.setProperty('right', 'auto', 'important');
            frame.style.setProperty('bottom', 'auto', 'important');
            frame.style.setProperty('width', `${width}px`, 'important');
            frame.style.setProperty('max-width', `${width}px`, 'important');
            frame.style.setProperty('height', `${height}px`, 'important');
            frame.style.setProperty('max-height', `${height}px`, 'important');
            frame.style.setProperty('transform', 'none', 'important');
            frame.style.setProperty('transform-origin', 'top left', 'important');
            frame.style.setProperty('overflow', 'hidden', 'important');
            frame.style.setProperty('border-radius', '999px', 'important');
            frame.style.setProperty('z-index', '2147482998', 'important');
            frame.style.setProperty('pointer-events', 'auto', 'important');

            this._compactGorgiasCampaignContents(frame);
        });
    }

    _compactGorgiasCampaignContents(frame) {
        try {
            const doc = frame.contentWindow?.document;
            if (!doc?.head) return;
            if (doc.querySelector('style[data-doto-gorgias-campaign-style]')) return;

            const style = doc.createElement('style');
            style.setAttribute('data-doto-gorgias-campaign-style', '');
            style.textContent = `
                html, body {
                    margin: 0 !important;
                    padding: 0 !important;
                    overflow: hidden !important;
                }

                button, [role="button"] {
                    min-height: 40px !important;
                    height: 40px !important;
                    max-height: 40px !important;
                    padding: 0 12px !important;
                    border-radius: 999px !important;
                    font-size: 12px !important;
                    line-height: 1.15 !important;
                    white-space: nowrap !important;
                    overflow: hidden !important;
                    text-overflow: ellipsis !important;
                }
            `;
            doc.head.appendChild(style);
        } catch (error) {
            /* Some widget builds prevent direct iframe access.
               Outer iframe positioning still works in that case. */
        }
    }

    _restoreGorgiasCampaigns() {
        document.querySelectorAll('[data-doto-gorgias-campaigns]').forEach((frame) => {
            [
                'position', 'top', 'left', 'right', 'bottom',
                'width', 'max-width', 'height', 'max-height',
                'transform', 'transform-origin', 'overflow', 'border-radius', 'z-index', 'pointer-events'
            ].forEach((property) => frame.style.removeProperty(property));
            frame.removeAttribute('data-doto-gorgias-campaigns');
        });
    }

    _hideNativeGorgiasLauncher() {
        document
            .querySelectorAll(
                '#gorgias-chat-container iframe#chat-button, iframe#chat-button'
            )
            .forEach((launcher) => {
                launcher.setAttribute(
                    'data-doto-gorgias-native-launcher',
                    ''
                );
                launcher.style.setProperty(
                    'display',
                    'none',
                    'important'
                );
                launcher.style.setProperty(
                    'visibility',
                    'hidden',
                    'important'
                );
                launcher.style.setProperty(
                    'pointer-events',
                    'none',
                    'important'
                );
            });
    }

    _restoreNativeGorgiasLauncher() {
        document
            .querySelectorAll('[data-doto-gorgias-native-launcher]')
            .forEach((launcher) => {
                launcher.style.removeProperty('display');
                launcher.style.removeProperty('visibility');
                launcher.style.removeProperty('pointer-events');
                launcher.removeAttribute(
                    'data-doto-gorgias-native-launcher'
                );
            });
    }

    _createCustomGorgiasLauncher() {
        if (this._customGorgiasLauncher?.isConnected) return;

        const button = document.createElement('button');

        button.type = 'button';
        button.className = 'doto-gorgias-launcher';
        button.setAttribute('aria-label', 'Abrir chat de ayuda');

        button.innerHTML = `
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path
                    d="M4.5 4.75h15A2.75 2.75 0 0 1 22.25 7.5v7A2.75 2.75 0 0 1 19.5 17.25H10l-5.75 3v-3.82A2.75 2.75 0 0 1 1.75 13.7V7.5A2.75 2.75 0 0 1 4.5 4.75Z"
                    fill="currentColor"
                />
                <circle cx="7.6" cy="11" r="1.15" fill="white"/>
                <circle cx="12" cy="11" r="1.15" fill="white"/>
                <circle cx="16.4" cy="11" r="1.15" fill="white"/>
            </svg>
        `;

        button.addEventListener('click', () => {
            this._getGorgiasInitPromise().then(() => {
                if (window.GorgiasChat?.open) {
                    window.GorgiasChat.open();
                }
            });
        });

        document.body.appendChild(button);
        this._customGorgiasLauncher = button;
    }

    _removeCustomGorgiasLauncher() {
        this._customGorgiasLauncher?.remove();
        this._customGorgiasLauncher = null;
    }

    _findFixedAncestor(element) {
        let node = element;
        let depth = 0;

        while (node && node !== document.body && depth < 8) {
            const style = window.getComputedStyle(node);

            if (style.position === 'fixed') {
                return node;
            }

            node = node.parentElement;
            depth += 1;
        }

        return null;
    }

    _toggleLoading(state) {
        return () => {
            this.classList.toggle(CN_LOADING, state);
        }
    }

    _handleVariantUnavailability() {
        $toggleDisplay(this, true);

        window.requestAnimationFrame(() => {
            this._handleTrigger();
        });
    }

    set active(state) {
        this.classList.toggle(CN_ACTIVE, state);
        this._syncBodyOffset(state);

        if (this._isMobile()) {
            document.body.classList.toggle(
                'doto-sticky-buy-visible',
                state
            );

            this._syncCustomWhatsAppLauncher();
            this._syncCustomGorgiasLauncher();
            this._syncGorgiasCampaigns();
        } else {
            document.body.classList.remove(
                'doto-sticky-buy-visible'
            );
        }
    }
});
